const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const compactDateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

const numberFormatter = new Intl.NumberFormat();
const SWU_SOURCE_OFFSET = "+08:00";

export function parseTimestamp(value: string): Date | null {
  const explicitZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value);
  const plain =
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(\.\d+)?)?$/.exec(
      value,
    );
  const normalized =
    !explicitZone && plain
      ? `${plain[1]}-${plain[2]}-${plain[3]}T${plain[4]}:${plain[5]}:${plain[6] || "00"}${plain[7] || ""}${SWU_SOURCE_OFFSET}`
      : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(
  value: string | null | undefined,
  compact = false,
): string {
  if (!value) return "—";
  const date = parseTimestamp(value);
  if (!date) return "—";
  return (compact ? compactDateTimeFormatter : dateTimeFormatter).format(date);
}

export function formatBytes(value: number | null | undefined): string {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let size = bytes / 1024;
  let unit = units[0];
  for (let index = 1; index < units.length && size >= 1024; index += 1) {
    size /= 1024;
    unit = units[index];
  }
  return `${size >= 10 ? size.toFixed(1) : size.toFixed(2)} ${unit}`;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.max(0, Math.floor(seconds))} 秒`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} 分钟`;
  if (seconds < 86400) {
    return `${Math.floor(seconds / 3600)} 小时 ${Math.floor((seconds % 3600) / 60)} 分钟`;
  }
  return `${Math.floor(seconds / 86400)} 天 ${Math.floor((seconds % 86400) / 3600)} 小时`;
}

export function formatNumber(value: number | null | undefined): string {
  return numberFormatter.format(Number(value || 0));
}

export function actionLabel(action: string): string {
  const labels: Record<string, string> = {
    "admin.login.failed": "登录失败",
    "admin.login.succeeded": "登录成功",
    "admin.logout": "退出登录",
    "user.data.refresh": "刷新用户数据",
    "user.session.revoke": "撤销单个用户会话",
    "user.sessions.revoke": "撤销用户会话",
    "user.delete": "删除用户",
    "snapshot.delete": "删除快照",
    "job.run": "触发定时任务",
    "publication.create": "发布公告或通知",
    "publication.delete": "软删除公告或通知",
    "publication.restore": "恢复公告或通知",
    "publication.media.upload": "上传公告图片",
    "auto-dorm-check.feature.update": "调整自动查寝全局设置",
  };
  return labels[action] || action;
}

export function resourceLabel(resource: string): string {
  if (resource === "grades") return "成绩";
  if (resource === "schedule") return "本地日程同步副本";
  if (resource === "messages") return "教务消息预览";
  if (resource === "notices") return "学校通知预览";
  if (resource === "electricity") return "寝室绑定与电费";
  if (resource === "exams:catalog") return "考试可用学期";
  if (resource.startsWith("exams:")) {
    return `考试安排 · ${resource.slice("exams:".length)}`;
  }
  if (resource.startsWith("timetable:")) {
    const semester = resource.slice("timetable:".length);
    return semester === "default" ? "当前课表" : `课表 · ${semester}`;
  }
  return resource;
}

export function profileEntries(
  profile: Record<string, unknown>,
): Array<{ key: string; label: string; value: string }> {
  const labels: Record<string, string> = {
    name: "姓名",
    accountId: "学号",
    phone: "绑定手机号",
    registeredPhone: "登记手机号",
    homeAddress: "家庭地址",
    gender: "性别",
    dateOfBirth: "出生日期",
    nativePlace: "籍贯",
    ethnicity: "民族",
    nationality: "国籍",
    idType: "证件类型",
    idNumber: "证件号码",
    studentOrigin: "生源地",
    enrollmentDate: "入学日期",
    accommodationStatus: "住宿状态",
    accommodationLocation: "住宿地点",
    grade: "所在年级",
    organizationName: "学院 / 单位",
    majorName: "专业",
    className: "班级",
    programLength: "学制",
    studentType: "学生类别",
    studentStatus: "学籍状态",
  };
  const fieldOrder = Object.keys(labels);

  return Object.entries(profile)
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    )
    .sort(([left], [right]) => {
      const leftIndex = fieldOrder.indexOf(left);
      const rightIndex = fieldOrder.indexOf(right);
      if (leftIndex === -1 && rightIndex === -1)
        return left.localeCompare(right);
      if (leftIndex === -1) return 1;
      if (rightIndex === -1) return -1;
      return leftIndex - rightIndex;
    })
    .map(([key, value]) => ({
      key,
      label:
        labels[key] ||
        key
          .replace(/([a-z\d])([A-Z])/g, "$1 $2")
          .replace(/[_-]+/g, " ")
          .replace(/^./, (character) => character.toUpperCase()),
      value:
        typeof value === "object"
          ? JSON.stringify(value, null, 2)
          : String(value),
    }));
}
